import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { datasets as datasetsApi, sessions as sessionsApi, challenges as challengesApi } from '../api/client';

const BALANCES = [10000, 25000, 50000, 100000, 200000];

export default function SessionSetupPage() {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [startMode, setStartMode] = useState('random');
  const [startIndex, setStartIndex] = useState(50);
  const [capital, setCapital] = useState('10000');
  const [spread, setSpread] = useState('0');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Challenge mode
  const [mode, setMode] = useState('free'); // 'free' | 'challenge'
  const [challengeConfigs, setChallengeConfigs] = useState([]);
  const [challengeType, setChallengeType] = useState('2step_phase1');
  const [accountType, setAccountType] = useState('standard');
  const [selectedBalance, setSelectedBalance] = useState(50000);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);

  useEffect(() => {
    datasetsApi.get(datasetId)
      .then((res) => {
        setDataset(res.data);
        setStartIndex(Math.min(50, Math.floor(res.data.bar_count / 2)));
      })
      .catch(() => setError('Dataset not found.'))
      .finally(() => setLoading(false));

    challengesApi.list().then(res => setChallengeConfigs(res.data)).catch(() => {});
  }, [datasetId]);

  // Auto-select challenge config
  useEffect(() => {
    const match = challengeConfigs.find(c =>
      c.challenge_type === challengeType && c.account_type === accountType
    );
    if (match) {
      setSelectedChallengeId(match.id);
      if (mode === 'challenge') {
        setCapital(String(selectedBalance));
      }
    }
  }, [challengeType, accountType, challengeConfigs, selectedBalance, mode]);

  const selectedChallenge = challengeConfigs.find(c => c.id === selectedChallengeId);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreating(true);

    try {
      const data = {
        dataset: parseInt(datasetId),
        name: name || `Session ${new Date().toLocaleDateString()}`,
        starting_capital: parseFloat(capital),
        spread_points: parseFloat(spread) || 0,
      };

      if (startMode === 'random') {
        const minStart = Math.min(50, Math.floor(dataset.bar_count * 0.1));
        const maxStart = Math.max(minStart + 1, dataset.bar_count - 100);
        data.start_bar_index = Math.floor(Math.random() * (maxStart - minStart)) + minStart;
      } else if (startMode === 'date') {
        if (startDate) data.start_date = new Date(startDate).toISOString();
        if (endDate) data.end_date = new Date(endDate).toISOString();
        if (!startDate) data.start_bar_index = 0;
      } else {
        data.start_bar_index = startIndex;
      }

      if (mode === 'challenge' && selectedChallengeId) {
        data.challenge = selectedChallengeId;
      }
      const res = await sessionsApi.create(data);
      navigate(`/replay/${res.data.id}`);
    } catch (err) {
      const d = err.response?.data;
      setError(d ? Object.values(d).flat().join(', ') || 'Failed.' : 'Failed to create session.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="page">Loading...</div>;
  if (!dataset) return <div className="page"><div className="error-msg">{error}</div></div>;

  return (
    <div className="page">
      <h2>New Replay Session</h2>

      <div className="card" style={{ maxWidth: 600, marginTop: '1rem' }}>
        <div className="dataset-meta" style={{ marginBottom: '1rem' }}>
          <span className="badge">{dataset.instrument}</span>
          <span className="badge">{dataset.timeframe}</span>
          <span>{dataset.bar_count.toLocaleString()} bars</span>
        </div>

        {/* Mode toggle */}
        <div className="challenge-mode-toggle">
          <button className={`challenge-mode-btn ${mode === 'free' ? 'active' : ''}`}
            onClick={() => setMode('free')}>Free Practice</button>
          <button className={`challenge-mode-btn ${mode === 'challenge' ? 'active' : ''}`}
            onClick={() => setMode('challenge')}>Prop Firm Challenge</button>
        </div>

        {/* Challenge configuration */}
        {mode === 'challenge' && (
          <div className="challenge-setup">
            {/* Challenge type */}
            <div className="challenge-type-tabs">
              <button className={`challenge-type-tab ${challengeType === '2step_phase1' ? 'active' : ''}`}
                onClick={() => setChallengeType('2step_phase1')}>
                <strong>2-Step</strong><small>Phase 1</small>
              </button>
              <button className={`challenge-type-tab ${challengeType === '2step_phase2' ? 'active' : ''}`}
                onClick={() => setChallengeType('2step_phase2')}>
                <strong>2-Step</strong><small>Phase 2</small>
              </button>
              <button className={`challenge-type-tab ${challengeType === '1step' ? 'active' : ''}`}
                onClick={() => setChallengeType('1step')}>
                <strong>1-Step</strong><small>Single Phase</small>
              </button>
            </div>

            {/* Account type */}
            <div className="challenge-account-type">
              <button className={`challenge-acct-btn ${accountType === 'standard' ? 'active' : ''}`}
                onClick={() => setAccountType('standard')}>
                <strong>Standard</strong>
                <small>1:100 Intraday</small>
              </button>
              <button className={`challenge-acct-btn ${accountType === 'swing' ? 'active' : ''}`}
                onClick={() => setAccountType('swing')}>
                <strong>Swing</strong>
                <small>1:30 Overnight + News</small>
              </button>
            </div>

            {/* Balance selector */}
            <div className="challenge-balance-grid">
              {BALANCES.map(b => (
                <button key={b}
                  className={`challenge-balance-btn ${selectedBalance === b ? 'active' : ''}`}
                  onClick={() => { setSelectedBalance(b); setCapital(String(b)); }}>
                  ${b.toLocaleString()}
                </button>
              ))}
            </div>

            {/* Rules summary */}
            {selectedChallenge && (
              <div className="challenge-rules-card">
                <h4>Challenge Rules</h4>
                <div className="challenge-rules-grid">
                  <div className="challenge-rule">
                    <span className="challenge-rule-label">Profit Target</span>
                    <span className="challenge-rule-value pnl-positive">
                      {selectedChallenge.profit_target_pct}% (${(selectedBalance * selectedChallenge.profit_target_pct / 100).toLocaleString()})
                    </span>
                  </div>
                  <div className="challenge-rule">
                    <span className="challenge-rule-label">Daily Loss Limit</span>
                    <span className="challenge-rule-value pnl-negative">
                      {selectedChallenge.daily_loss_limit_pct}% (${(selectedBalance * selectedChallenge.daily_loss_limit_pct / 100).toLocaleString()})
                    </span>
                  </div>
                  <div className="challenge-rule">
                    <span className="challenge-rule-label">Max Drawdown</span>
                    <span className="challenge-rule-value pnl-negative">
                      {selectedChallenge.max_drawdown_pct}% (${(selectedBalance * selectedChallenge.max_drawdown_pct / 100).toLocaleString()})
                    </span>
                  </div>
                  <div className="challenge-rule">
                    <span className="challenge-rule-label">Min Trading Days</span>
                    <span className="challenge-rule-value">{selectedChallenge.min_trading_days} days</span>
                  </div>
                  <div className="challenge-rule">
                    <span className="challenge-rule-label">Max Period</span>
                    <span className="challenge-rule-value">{selectedChallenge.max_calendar_days} days</span>
                  </div>
                  <div className="challenge-rule">
                    <span className="challenge-rule-label">Leverage</span>
                    <span className="challenge-rule-value">1:{selectedChallenge.leverage}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label>Session Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          </div>

          {mode === 'free' && (
            <>
              <div className="form-group">
                <label>Starting Capital</label>
                <input type="number" value={capital} onChange={(e) => setCapital(e.target.value)} min="1" step="0.01" required />
              </div>
              <div className="form-group">
                <label>Spread (points)</label>
                <input type="number" value={spread} onChange={(e) => setSpread(e.target.value)} min="0" step="0.00001" />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Starting Point</label>
            <div className="radio-group">
              <label className="radio-label">
                <input type="radio" name="startMode" value="random" checked={startMode === 'random'}
                  onChange={() => setStartMode('random')} /> Random (recommended)
              </label>
              <label className="radio-label">
                <input type="radio" name="startMode" value="date" checked={startMode === 'date'}
                  onChange={() => setStartMode('date')} /> Date Range
              </label>
              <label className="radio-label">
                <input type="radio" name="startMode" value="manual" checked={startMode === 'manual'}
                  onChange={() => setStartMode('manual')} /> Bar Index
              </label>
            </div>
          </div>

          {startMode === 'date' && (
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Start Date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  min={dataset.date_start?.slice(0, 10)} max={dataset.date_end?.slice(0, 10)} required />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>End Date <small style={{ opacity: 0.5 }}>(optional)</small></label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || dataset.date_start?.slice(0, 10)} max={dataset.date_end?.slice(0, 10)} />
              </div>
            </div>
          )}

          {startMode === 'manual' && (
            <div className="form-group">
              <label>Start at bar index (0 - {dataset.bar_count - 1})</label>
              <input type="number" value={startIndex}
                onChange={(e) => setStartIndex(parseInt(e.target.value) || 0)}
                min="0" max={dataset.bar_count - 1} required />
            </div>
          )}

          <button type="submit" className="btn" disabled={creating} style={{ marginTop: '1rem', width: '100%' }}>
            {creating ? 'Creating...' : mode === 'challenge' ? 'Start Challenge' : 'Start Replay'}
          </button>
        </form>
      </div>
    </div>
  );
}
